import { CommonModule, DatePipe, DecimalPipe, JsonPipe, NgFor, ɵnormalizeQueryParams } from '@angular/common';
import { Component, DestroyRef, NgModule, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import { FormControl, FormGroup, FormsModule, NgModel, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatOptionModule, provideNativeDateAdapter } from '@angular/material/core';
import { MatDatepickerModule, MatDateRangeInput } from '@angular/material/datepicker';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar, MatSnackBarModule, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { Router } from '@angular/router';
import { IgxGridModule } from '@infragistics/igniteui-angular';
import { IgxGridComponent, IgxTabsModule } from '@infragistics/igniteui-angular';
import { AppGeneralStateService } from 'src/app/app-general-state.service';
import { AuthService } from 'src/app/core/services/auth-security/auth.service';
import { MessageService } from 'src/app/core/services/broadcast/message-service';
import { DataService } from 'src/app/core/services/data/data.service';
import { MessageDialogService } from 'src/app/core/services/helper/message-dialog.service';
import { ParkerStateService } from 'src/app/core/services/state/parker-state.service';
import { HeaderTitleLotDataComponent } from 'src/app/shared/ui/header-title-lot-data/header-title-lot-data.component';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { environment } from 'src/environments/environment';
import {
  IBuildCompanyInvoiceRequest,
  ICompaniesShortList,
  ICompanyActiveShortListResponse,
  ILotInvoiceMiSettingsResponse,
  IMiNewInvoiceLine,
  IParkerActiveShortListResponse,
  IPrelimInvoiceListResponse,
  IManualInvoiceNewPrlimWithTax_Request,
  IManualInvoice_Request,
  IManualInvoiceCustomer_Request,
  IBillingDetailWithTax_Request,
  IInvoiceSystemGenerationDefaults,
  IInvoicePrelimRecipientNotificationDetailsResponse,
} from 'src/app/shared/interfaces';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { aceDates } from 'src/app/shared/helpers/transform/aceDates';
import { DataType } from 'igniteui-angular';
import { MatButtonModule } from '@angular/material/button';
import { RevenueTypes } from 'src/app/shared/entities/RevenueTypes';
import { InvoiceService } from 'src/app/core/services/data/invoice.service';
import { LotService } from 'src/app/core/services/data/lot.service';
import { ActiveTaxList } from 'src/app/shared/entities/ActiveTaxList';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmRemoveTaxComponent } from 'src/app/quick-search/dialog/confirm-remove-tax.component';
import { ConfirmAddTaxComponent } from 'src/app/quick-search/dialog/confirm-add-tax.component';
import { AddMiInvoiceLineNonTaxableComponent } from './add-mi-invoice-line-non-taxable/add-mi-invoice-line-non-taxable.component';
import { AddMiInvoiceLineTaxableComponent } from './add-mi-invoice-line-taxable/add-mi-invoice-line-taxable.component';
import { YearSelectorDirectiveStandalone } from 'src/app/shared/directives/set-date-current-year-standalone.directive';
import { formatDate } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TitleSettings } from 'igniteui-angular-charts';
import { DialogMiPreviewComponent } from 'src/app/core/dialog/dialog-mi-preview/dialog-mi-preview.component';
import { DialogMiSendInvoiceComponent } from 'src/app/core/dialog/dialog-mi-send-invoice/dialog-mi-send-invoice.component';
import {
  IMiInvoiceSubmitRequest,
  IMiInvoiceData,
  IMiInvoiceBillingLine,
  IMiInvoiceCustomer,
  Mi_PrelimInvoiceCreationReturn,
} from 'src/app/shared/code/interfaces/mi-invoice-interfaces';
import { MiNewInvoiceLineComponent } from './mi-new-invoice-line/mi-new-invoice-line.component';

@Component({
  selector: 'app-generate-mi',
  standalone: true,
  imports: [
    MatSlideToggleModule,
    MatExpansionModule,
    MatIconModule,
    CommonModule,
    MatTabsModule,
    MatSnackBarModule,
    MatTableModule,
    MatCardModule,
    MatOptionModule,
    MatInputModule,
    MatFormFieldModule,
    MatDatepickerModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    HeaderTitleLotDataComponent,
    MatSnackBarModule,
    MatProgressBarModule,
    HeaderTitleLotDataComponent,
    MatTableModule,
    MatCheckboxModule,
    FormsModule,
    MatButtonToggleModule,
    MatButtonModule,
    MatTooltipModule,
    MiNewInvoiceLineComponent,
  ],
  templateUrl: './generate-mi.component.html',
  providers: [provideNativeDateAdapter()],
  styleUrl: './generate-mi.component.scss',
})
export class GenerateMiComponent implements OnInit {
  urlDomain: string = '';
  queryInProcess: boolean = false;
  queryCompleted: boolean = false;
  currentStep: string = 'Purging Previous Invoices';
  prelimInvoiceList: IPrelimInvoiceListResponse[] = [];
  currentSearchType: string = 'parker';
  companyList: ICompanyActiveShortListResponse[] = [];
  parkerList: IParkerActiveShortListResponse[] = [];
  isCompanyListLoading: boolean = false;
  isParkerListLoading: boolean = false;
  currentEntityId: string = '';
  selectedParkerId: string = 'ALL';
  selectedCompanyId: string = 'ALL';
  selectedParker?: IParkerActiveShortListResponse;
  selectedCompany?: ICompanyActiveShortListResponse;
  invoiceCreationSettings: ILotInvoiceMiSettingsResponse = undefined as any;
  revenueTypes: RevenueTypes[] = [];
  activeTaxListForLot: ActiveTaxList[] = [];
  totalTaxRate: number = 0;
  grossTotalTaxRate: number = 0;
  addRevenueIsTaxableCheckBoxGross: boolean = true;
  taxableRevenueShouldBeApplied: boolean = true;
  netRate: number = 0;
  revenueCodeSelectedNet: string = '';
  miFormData: IMiNewInvoiceLine[] = [];
  isTaxable: boolean = true;
  currentLotNumber: string;
  invoiceDescription: string = '';
  createInvoiceDisabled: boolean = true;
  targetForInvoiceSelected: boolean = false;
  invoiceSystemDefaults: IInvoiceSystemGenerationDefaults = undefined as any;
  isInvoiceDateReadOnly: boolean = false;
  deliverySettings: IInvoicePrelimRecipientNotificationDetailsResponse | null = null;
  isCapableOfSendingEmail: boolean = false;

  //Net Calculator Vars
  netRateValue: number = 0;
  netQuantity: number = 0;
  netTotalTaxRate: number = 0;
  netDisplayTotalNet: number = 0;
  netDisplayTaxAmount: number = 0;
  netDisplayTotalAmount: number = 0;
  revenueDescriptionNet: string = '';
  inputValidatedNet: boolean = false;
  addRevenueIsTaxableCheckBoxNet: boolean = true;

  displayTotalTax: number = 0;
  displaySubtotal: number = 0;
  displayGrandTotal: number = 0;

  preLimMiForm = new FormGroup({
    revenueCode: new FormControl('', []),
    dueDate: new FormControl(new Date(), []),
    invoiceDate: new FormControl(new Date(), []),
    description: new FormControl('', []),
    rate: new FormControl('', []),
    quantity: new FormControl('', []),
    includeTax: new FormControl('', []),
    sequenceStart: new FormControl('', []),
    sequenceEnd: new FormControl('', []),
  });

  //Services
  _appGeneralState = inject(AppGeneralStateService);
  _datePipe = inject(DatePipe);
  _dataService = inject(DataService);
  _router = inject(Router);
  _destroyRefService = inject(DestroyRef);
  _dialogMessageService = inject(MessageDialogService);
  _matSnackBar = inject(MatSnackBar);
  #_invoiceService = inject(InvoiceService);
  #_lotService = inject(LotService);
  #_matDialog = inject(MatDialog);
  #_decimalPipe = inject(DecimalPipe);

  constructor() {
    this.urlDomain = environment.UrlDomain;
    this.currentLotNumber = this._appGeneralState.currentLotNumber;

    this.setInvoiceDefaultValues(this._appGeneralState.currentLotNumber);
  }

  ngOnInit(): void {
    this.currentSearchType = 'parker';
    this.getCompanyListForLotNumber(this._appGeneralState.currentLotNumber);
    this.getParkerListForLotNumber(this._appGeneralState.currentLotNumber);
    this.getMiInvoiceCreationSettings(this._appGeneralState.currentLotNumber);
    this.getRevenueTypesForLotNumber(this._appGeneralState.currentLotNumber);
    this.getMiInvoiceDateReadonlySetting(this._appGeneralState.currentLotNumber);

    this._appGeneralState.lotNumberSubject.subscribe({
      next: (v) => this.onLotNumberChange(v),
    });
  }

  onLotNumberChange(newLotNumber: string): void {
    this.currentLotNumber = newLotNumber;

    // Reset all state to initial values
    this.queryInProcess = false;
    this.queryCompleted = false;
    this.currentStep = 'Purging Previous Invoices';
    this.prelimInvoiceList = [];
    this.currentSearchType = 'parker';
    this.companyList = [];
    this.parkerList = [];
    this.currentEntityId = '';
    this.selectedParkerId = 'ALL';
    this.selectedCompanyId = 'ALL';
    this.selectedParker = undefined as any;
    this.selectedCompany = undefined as any;
    this.invoiceSystemDefaults = undefined as any;
    this.revenueTypes = [];
    this.activeTaxListForLot = [];
    this.totalTaxRate = 0;
    this.grossTotalTaxRate = 0;
    this.addRevenueIsTaxableCheckBoxGross = true;
    this.taxableRevenueShouldBeApplied = true;
    this.netRate = 0;
    this.revenueCodeSelectedNet = '';
    this.miFormData = [];
    this.isTaxable = true;
    this.invoiceDescription = '';
    this.createInvoiceDisabled = true;
    this.targetForInvoiceSelected = false;
    this.netRateValue = 0;
    this.netQuantity = 0;
    this.netTotalTaxRate = 0;
    this.netDisplayTotalNet = 0;
    this.netDisplayTaxAmount = 0;
    this.netDisplayTotalAmount = 0;
    this.revenueDescriptionNet = '';
    this.inputValidatedNet = false;
    this.addRevenueIsTaxableCheckBoxNet = true;
    this.displayTotalTax = 0;
    this.displaySubtotal = 0;
    this.displayGrandTotal = 0;

    this.preLimMiForm.reset({
      revenueCode: '',
      dueDate: new Date(),
      invoiceDate: new Date(),
      description: '',
      rate: '',
      quantity: '',
      includeTax: '',
      sequenceStart: '',
      sequenceEnd: '',
    });

    // Re-fetch data for the new lot
    this.getRevenueTypesForLotNumber(newLotNumber);
    this.getCompanyListForLotNumber(newLotNumber);
    this.getParkerListForLotNumber(newLotNumber);
    this.getMiInvoiceCreationSettings(newLotNumber);
    this.getMiInvoiceDateReadonlySetting(newLotNumber);
  }

  evalCompanyOrParkerSelection(): void {
    this.targetForInvoiceSelected = false;
    if (this.currentSearchType == 'parker') {
      this.selectedParkerId = 'ALL';
    } else this.selectedCompanyId = 'ALL';
  }

  isCompanyOrParkerSelected(): boolean {
    var isSelected: boolean = false;

    var companySelected = this.selectedCompanyId != 'ALL';

    var parkerSelected = this.selectedParkerId != 'ALL';

    return companySelected || parkerSelected;
  }

  onResetInvoiceLineData(): void {
    var dateHelper = new aceDates();

    const dueDate: Date = dateHelper.dateAdd(new Date(), 'day', 7);

    this.preLimMiForm.patchValue({
      revenueCode: '',
      description: '',
      rate: '',
      quantity: '',
      includeTax: '',
      sequenceStart: '',
      sequenceEnd: '',
    });
  }

  onDeleteInvoiceLine(index: number): void {
    var totalTax: number = 0;
    var totalAmount: number = 0;
    var subtotal: number = 0;

    this.miFormData.splice(index, 1);

    if (this.miFormData.length > 0) {
      let ii: number = 0;
      for (ii = 0; ii < this.miFormData.length; ii++) {
        subtotal += this.miFormData[ii].quantity * this.miFormData[ii].rate;
        totalTax += this.miFormData[ii].taxAmount;
        totalAmount += this.miFormData[ii].totalAmount;
      }
    }

    this.displaySubtotal = subtotal;
    this.displayTotalTax = totalTax;
    this.displayGrandTotal = totalAmount;
  }

  handleAddNewInvoiceLine(eventData: any) {
    var totalTax: number = 0;
    var totalAmount: number = 0;
    var subtotal: number = 0;
    var netAmount: number = 0;
    var taxAmount: number = 0;

    console.log('eventData', eventData);

    this.miFormData.push(eventData);

    if (this.miFormData.length > 0) {
      let ii: number = 0;
      for (ii = 0; ii < this.miFormData.length; ii++) {
        subtotal += this.miFormData[ii].netAmount;
        totalTax += this.miFormData[ii].taxAmount;
        netAmount += this.miFormData[ii].netAmount;
        taxAmount += this.miFormData[ii].taxAmount;
        totalAmount += netAmount + taxAmount;
        this.miFormData[ii].totalAmount = this.miFormData[ii].taxAmount + this.miFormData[ii].netAmount;
      }
    }

    this.displaySubtotal = subtotal;
    this.displayTotalTax = totalTax;
    this.displayGrandTotal = subtotal + totalTax;
  }
  onCompanyNameChanged(event: string): void {}

  getParkerListForLotNumber(lotNumber: string) {
    this.isParkerListLoading = true;
    this._dataService.getParkerActiveShortListByLotNumber(lotNumber).subscribe(
      (res) => {
        this.parkerList = res;
        if (this.parkerList.length === 0) {
          this._matSnackBar.open('No Parker Paid Parkers Found for this Lot', undefined, {
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
          });
        }
      },
      (err: any) => {
        this.isParkerListLoading = false;
      },
      () => {
        this.isParkerListLoading = false;
      },
    );
  }

  getCompanyListForLotNumber(lotNumber: string) {
    this.isCompanyListLoading = true;
    this._dataService.getCompanyActiveShortListByLotNumber(lotNumber).subscribe(
      (res) => {
        this.companyList = res;
      },
      (err: any) => {
        this.isCompanyListLoading = false;
      },
      () => {
        this.isCompanyListLoading = false;
      },
    );
  }

  getMiInvoiceCreationSettings(lotNumber: string) {
    this._dataService.getLotMiInvoiceSettings(lotNumber).subscribe(
      (res) => {
        this.invoiceCreationSettings = res;
      },
      (err: any) => {},
      () => {
        this.setDates();
      },
    );
  }

  getMiInvoiceDateReadonlySetting(lotNumber: string) {
    this._dataService.getMiInvoiceDateReadOnlyStatus(lotNumber).subscribe(
      (res) => {
        this.isInvoiceDateReadOnly = res;
      },
      (err: any) => {},
      () => {
        this.isInvoiceDateReadOnly
          ? this.preLimMiForm.get('invoiceDate')?.disable()
          : this.preLimMiForm.get('invoiceDate')?.enable();
      },
    );
  }

  onParkerSelectionChange(): void {
    this.selectedParker = this.parkerList.find((p) => p.parkerId == this.selectedParkerId);
    this.targetForInvoiceSelected = !!this.selectedParker;
  }

  onCompanySelectionChange(): void {
    this.selectedCompany = this.companyList.find((c) => c.companyId == this.selectedCompanyId);
    this.targetForInvoiceSelected = !!this.selectedCompany;
  }

  setDates(): void {
    try {
      var dateHelper = new aceDates();
      const dueDate: Date = dateHelper.dateAdd(new Date(), 'day', this.invoiceCreationSettings.miInvoiceDueDate);
      this.preLimMiForm.patchValue({
        dueDate: new Date(dueDate),
        invoiceDate: new Date(),
      });
      // this.invoiceCreationSettings.isMiInvoiceDateReadOnly
      //   ? this.preLimMiForm.get('invoiceDate')?.disable()
      //   : this.preLimMiForm.get('invoiceDate')?.enable();

      this.invoiceCreationSettings.allowMiDueDateEdits == false
        ? this.preLimMiForm.get('dueDate')?.disable()
        : this.preLimMiForm.get('dueDate')?.enable();
    } catch (err) {}
  }

  getRevenueTypesForLotNumber(lotNumber: string) {
    this.#_invoiceService.getRevenueTypesForLotNumber(lotNumber).subscribe({
      next: (res) => {
        this.revenueTypes = res;
      },
      error: (_err: any) => {},
      complete: () => {
        this.getActiveTaxListForLotNumber(this._appGeneralState.currentLotNumber);
        this.revenueTypes = this.revenueTypes.filter((rt) => !rt.isExempt);
      },
    });
  }

  getActiveTaxListForLotNumber(lotNumber: string) {
    this.#_lotService.getActiveTaxListByLotNumber(lotNumber).subscribe(
      (taxListForLot) => {
        this.activeTaxListForLot = taxListForLot;
      },
      (err: any) => {},
      () => {
        this.grossTotalTaxRate = this.calculateTotalTaxRate();
      },
    );
  }

  calculateTotalTaxRate(): number {
    let i: number = 0;
    let totalTaxRate: number = 0;

    for (i = 0; i < this.activeTaxListForLot.length; i++) {
      totalTaxRate += this.activeTaxListForLot[i].taxAmount;
    }

    //set class level var
    this.totalTaxRate = totalTaxRate;

    return totalTaxRate;
  }

  confirmChangeTaxesFromAddedRevenue() {
    if (this.taxableRevenueShouldBeApplied === true) {
      const dialogRef = this.#_matDialog.open(ConfirmRemoveTaxComponent, {
        width: '900px',
        height: '270px',
        position: { top: '75px' },
      });

      //return when dialog closed and process if proceed or cancel
      dialogRef.afterClosed().subscribe((result) => {
        if (result == true) {
          //   if (this.typeRevenueCalculation == 'GROSS') this.addRevenueIsTaxableCheckBoxGross = false;
          //   else this.addRevenueIsTaxableCheckBoxNet = false;

          //   this.taxableRevenueShouldBeApplied = false;
          // } else {
          //   if (this.typeRevenueCalculation == 'GROSS') this.addRevenueIsTaxableCheckBoxGross = true;
          //   else this.addRevenueIsTaxableCheckBoxNet = true;

          this.taxableRevenueShouldBeApplied = true;
        }

        // this.onCalculateRateFromNetRate();
      });
    } else {
      const dialogTurnTaxOnRef = this.#_matDialog.open(ConfirmAddTaxComponent, {});
      //     dialogTurnTaxOnRef.afterClosed().subscribe((result) => {
      //       if (result == true) {
      //         if (this.typeRevenueCalculation == 'GROSS') this.addRevenueIsTaxableCheckBoxGross = true;
      //         else this.addRevenueIsTaxableCheckBoxNet = true;

      //         this.taxableRevenueShouldBeApplied = true;
      //       } else {
      //         if (this.typeRevenueCalculation == 'GROSS') this.addRevenueIsTaxableCheckBoxGross = false;
      //         else this.addRevenueIsTaxableCheckBoxNet = false;

      //         this.taxableRevenueShouldBeApplied = false;
      //       }
      //       if (this.typeRevenueCalculation == 'GROSS') this.onCalculateRateFromGrossRate();
      //       else this.onCalculateRateFromNetRate();
      //     });
      //   }
      // }
    }
  }

  onCalculateRateFromNetRate(): void {
    //don't calc if we don't have a gross rate or quantity
    if (this.netRateValue <= 0 || this.netQuantity <= 0) return;

    //This is the calculation for determining the Rate when entering the Net Rate
    //Order of Operations as follows - It's important to do base calcs per unit prior to multiplying for quantity
    if (this.taxableRevenueShouldBeApplied) {
      //**** START OF GROSS CALC WITH TAX ****/

      //Step 1: Get the total Net Value
      this.netDisplayTotalNet = this.netRateValue * this.netQuantity;

      //Step 2: Get the total amount of tax
      this.netDisplayTaxAmount = this.netDisplayTotalNet * this.grossTotalTaxRate;

      //Step 3: Get the Total Amount = (Rate * Quantity) + (Tax Amount * Quantity)
      this.netDisplayTotalAmount = this.netDisplayTotalNet + this.netDisplayTaxAmount;

      //Step 4. Display only two decimals in the Net Rate UI element
      this.netRate = +this.#_decimalPipe.transform(this.netRate, '1.2-2')!;

      //**** END OF NET CALC WITH TAX ****/
    } else {
      //Same Order of Operations as taxable, except setting the tax amount = $0

      //**** START OF NET CALC WITHOUT TAX ****/
      this.netDisplayTotalNet = this.netRateValue * this.netQuantity;
      this.netDisplayTaxAmount = 0;
      this.netDisplayTotalAmount = this.netDisplayTotalNet + this.netDisplayTaxAmount;
      this.netRate = +this.#_decimalPipe.transform(this.netRate, '1.2-2')!;

      //**** END OF NET CALC WITHOUT TAX ****/
    }

    this.isNetRevenueAddValidated();
  }

  isNetRevenueAddValidated(): void {
    this.inputValidatedNet = false;

    if (this.revenueCodeSelectedNet == '') return;

    if (this.netRateValue == 0 || this.netRateValue == null) return;

    if (this.netQuantity == 0) return;

    if (this.revenueDescriptionNet == '') return;

    this.inputValidatedNet = true;
  }

  isRevenueTypeNonTaxOnly(revenueTypeCode: string): boolean {
    let ii: number = 0;
    let isOnlyNonTaxable = false;

    for (ii = 0; ii < this.revenueTypes.length; ii++) {
      if (this.revenueTypes[ii].code === revenueTypeCode) {
        isOnlyNonTaxable = this.revenueTypes[ii].isNonTaxableOnly === true;
      }
    }
    return isOnlyNonTaxable;
  }

  onGenerateMiPrelims(): void {}

  onChangeResetRevenueType(): void {
    const revCode: string = (this.preLimMiForm.get('revenueCode')?.value ?? '') as string;
    const isNonTaxable: boolean = this.isRevenueTypeNonTaxOnly(revCode);
  }

  submitMiForPrelimGeneration(): void {
    let invoiceDataResponse: Mi_PrelimInvoiceCreationReturn;

    const newMiPrelimData: IMiInvoiceSubmitRequest = this.createNewMiPrelimModel();

    // Set entityID
    this.currentEntityId = newMiPrelimData.invoiceCustomer.entityId;

    const snackBarRef = this._matSnackBar.open('Generating the new Manual Invoice Prelim...', 'Close', {
      horizontalPosition: 'center',
      verticalPosition: 'top',
      duration: undefined,
      panelClass: 'centered-snackbar',
    });

    this._dataService
      .mi_GeneratePrelimInvoices(newMiPrelimData)
      .pipe(takeUntilDestroyed(this._destroyRefService))
      .subscribe({
        next: (res) => {
          invoiceDataResponse = res;
        },
        error: (err: any) => {
          console.error('Prelim MI Generation Error', err);
          // close matsnackbar here
          snackBarRef.dismiss();
        },
        complete: () => {
          snackBarRef.dismiss();

          // The invoice number is whatever the server saved, which is not necessarily the first one
          // it generated - a collision makes it randomize the suffix.
          if (!invoiceDataResponse?.status || !invoiceDataResponse.invoiceNumber) {
            this._matSnackBar.open(
              invoiceDataResponse?.errorMessage || 'An error occurred while creating this Manual Invoice. Please try again.',
              'Close',
              {
                horizontalPosition: 'center',
                verticalPosition: 'top',
                duration: 5000,
                panelClass: 'error-snackbar',
              },
            );
            return;
          }

          this.showInvoicePdf(invoiceDataResponse.invoiceNumber, newMiPrelimData.invoiceCustomer.entityId);
        },
      });
  }

  getRecipientCustomerName(): string {
    var recipientName: string = '';

    if (this.currentSearchType == 'parker') {
      const parkerId: string = this.selectedParkerId;
      const parker = this.parkerList.find((item) => item.parkerId == parkerId);
      const lastName: string = parker?.lastName ?? '';
      const firstName: string = parker?.firstName ?? '';
      recipientName = `${lastName}, ${firstName}`;
    } else {
      const companyId: string = this.selectedCompanyId;
      const companyName: string = this.companyList.find((company) => company.companyId == companyId)?.companyName ?? '';
      recipientName = companyName;
    }

    return recipientName;
  }

  submitNewPrelimMi(newManualInvoiceData: IManualInvoiceNewPrlimWithTax_Request): void {
    let sequenceNumber: number;
    // this.displayManualInvoicePreview();

    // this._dataService
    //   .postPrelimManualInvoice(newManualInvoiceData)
    //   .pipe(takeUntilDestroyed(this._destroyRefService))
    //   .subscribe({
    //     next: (res) => {
    //       console.log('new mi submitted successfully');
    //     },
    //     error: (err: any) => {
    //       console.error('new mi error');
    //     },
    //     complete: () => {
    //       this.displayManualInvoicePreview();
    //     },
    //   });
  }

  displayManualInvoicePreview(): void {
    this.#_matDialog.open(DialogMiPreviewComponent, {
      data: {},
      hasBackdrop: false,
      height: '95vh',
      width: '90vw',
      maxWidth: '100vw',
      // panelClass: 'customer-dialog-border',
      position: {
        top: '30px',
        // left: '0px'
      },
    });
  }

  formatNewInvoiceNumber(lotNumber: string, sequenceNumber: number): string {
    const prefix: string = 'MI';
    const monthWip: number = new Date().getMonth() + 1;
    const yearWip: number = new Date().getFullYear();
    let lotNumberFormatted: string = '00000';
    let sequenceNumberFormatted: string = '00000';

    lotNumberFormatted = this.replaceStringAt(lotNumberFormatted, 5 - lotNumber.length, 5, lotNumber);
    sequenceNumberFormatted = this.replaceStringAt(
      sequenceNumberFormatted,
      5 - sequenceNumber.toString().length,
      5,
      sequenceNumber.toString(),
    );

    const month: string = monthWip < 10 ? '0' + monthWip.toString() : monthWip.toString();
    const year: string = yearWip.toString().substring(2, 4);
    const invoiceNumber: string = `MI${lotNumberFormatted}${month}${year}${sequenceNumberFormatted}`;

    return invoiceNumber;
  }

  async onGenerateMiPrelimInvoice(): Promise<void> {
    this.submitMiForPrelimGeneration();
  }

  replaceStringAt(originalString: string, startIndex: number, endIndex: number, replacement: string): string {
    if (startIndex < 0 || endIndex > originalString.length || startIndex >= endIndex) {
      return originalString; // Return original string if indices are invalid
    }

    const modifiedString = originalString.slice(0, startIndex) + replacement + originalString.slice(endIndex);
    return modifiedString;
  }

  newGuid() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0,
        v = c == 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  // NEW MI Interface Model
  createNewMiPrelimModel(): IMiInvoiceSubmitRequest {
    const manualInvoiceId: string = this.newGuid();
    let newMiInvoiceModel: IMiInvoiceSubmitRequest = {} as IMiInvoiceSubmitRequest;

    newMiInvoiceModel.invoiceData = this.createMiInvoiceDataModel();
    newMiInvoiceModel.invoiceCustomer = this.createMiInvoiceCustomerModel(this.newGuid()); // todo not sure we need this guid.  Might be created on the server
    newMiInvoiceModel.invoiceBillingLines = this.createMiInvoiceBillingLines(this.newGuid());
    return newMiInvoiceModel;
  }

  createMiInvoiceDataModel(): IMiInvoiceData {
    let miInvoiceData: IMiInvoiceData = {} as IMiInvoiceData;

    miInvoiceData.lotNumber = this._appGeneralState.currentLotNumber;
    miInvoiceData.recipient = this.getRecipientCustomerName();

    const invoiceDateValue = this.preLimMiForm.get('invoiceDate')?.value as Date | null;
    miInvoiceData.invoiceDate = invoiceDateValue ? formatDate(invoiceDateValue, 'yyyy-MM-dd HH:mm:ss', 'en-US') : '';

    const dueDateValue = this.preLimMiForm.get('dueDate')?.value as Date | null;
    miInvoiceData.dueDate = dueDateValue ? formatDate(dueDateValue, 'yyyy-MM-dd HH:mm:ss', 'en-US') : '';
    miInvoiceData.invoiceDescription = this.preLimMiForm.get('description')?.value ?? '';
    miInvoiceData.created = new Date();
    miInvoiceData.createdBy = this._appGeneralState.userName;

    // Assigned by the server during prelim creation; anything sent here is discarded.
    miInvoiceData.invoiceNumber = '';

    return miInvoiceData;
  }

  createMiInvoiceCustomerModel(manualInvoiceId: string): IMiInvoiceCustomer {
    let miInvoiceCustomer: IMiInvoiceCustomer = {} as IMiInvoiceCustomer;
    const selectedParker = this.selectedParker ?? ({} as IParkerActiveShortListResponse);
    const selectedCompany = this.selectedCompany ?? ({} as ICompanyActiveShortListResponse);

    console.log('CompanyId selected for invoice:', this.selectedCompanyId);
    console.log('ParkerId selected for invoice:', this.selectedParkerId);

    miInvoiceCustomer.manualInvoiceId = manualInvoiceId;
    miInvoiceCustomer.customerType = this.currentSearchType == 'parker' ? 'Parker' : 'Company';
    miInvoiceCustomer.entityId = this.currentSearchType == 'parker' ? this.selectedParkerId : this.selectedCompanyId;
    miInvoiceCustomer.customerName = this.getRecipientCustomerName();
    miInvoiceCustomer.billingAddress =
      this.currentSearchType == 'parker' ? selectedParker.address : selectedCompany.address;
    miInvoiceCustomer.city = this.currentSearchType == 'parker' ? selectedParker.city : selectedCompany.city;
    miInvoiceCustomer.state = this.currentSearchType == 'parker' ? selectedParker.state : selectedCompany.state;
    miInvoiceCustomer.zipCode = this.currentSearchType == 'parker' ? selectedParker.zipCode : selectedCompany.zipCode;
    miInvoiceCustomer.phone = this.currentSearchType == 'parker' ? selectedParker.phone : selectedCompany.phone;
    miInvoiceCustomer.emailAddress =
      this.currentSearchType == 'parker' ? selectedParker.emailAddress || '' : selectedCompany.emailAddress || '';

    console.log('EntityId set on invoice customer model:', miInvoiceCustomer.entityId);

    this.currentEntityId = miInvoiceCustomer.entityId;
    return miInvoiceCustomer;
  }

  createMiInvoiceBillingLines(manualInvoiceId: string): IMiInvoiceBillingLine[] {
    let miInvoiceBillingLines: IMiInvoiceBillingLine[] = [];
    const taxType: string = 'Tax'; // todo replace with actual tax type if needed.

    let ii: number = 0;
    for (ii = 0; ii < this.miFormData.length; ii++) {
      let newBilliingLine: IMiInvoiceBillingLine = {} as IMiInvoiceBillingLine;
      const line: IMiNewInvoiceLine = this.miFormData[ii];

      newBilliingLine.manualInvoiceId = manualInvoiceId;
      newBilliingLine.paymentType = line.revenueType;
      newBilliingLine.billingRate = line.rate; // need to subtract tax amount to get the billing rate for the prelim invoice since the rate coming from the form is the gross amount
      newBilliingLine.quantity = line.quantity;
      //newBilliingLine.flatRate = line.rate * line.quantity - line.taxAmount; // need to subtract tax amount to get the flat rate for the prelim invoice since the rate coming from the form is the gross amount
      newBilliingLine.sequenceStart = line.sequenceStart;
      newBilliingLine.sequenceEnd = line.sequenceEnd;
      newBilliingLine.tax = Math.round((line.taxAmount + Number.EPSILON) * 100) / 100;
      newBilliingLine.description = line.description;
      newBilliingLine.revCode = line.revenueCode;
      newBilliingLine.taxType = taxType;
      newBilliingLine.ratePerUnit = line.ratePerUnit;
      newBilliingLine.unitTaxAmount = line.unitTaxAmount;
      newBilliingLine.taxApplied = line.taxApplied;
      miInvoiceBillingLines.push(newBilliingLine);
    }

    return miInvoiceBillingLines;
  }

  // ++++++++++++++++++++++++++++++++++++
  // ===================================

  // EXISTING MI Interface Model
  prepNewManualInvoiceModel(invoiceNumber: string): IManualInvoiceNewPrlimWithTax_Request {
    const manualInvoiceId: string = this.newGuid();

    let manualPrelimInvoice: IManualInvoiceNewPrlimWithTax_Request = {} as IManualInvoiceNewPrlimWithTax_Request;
    manualPrelimInvoice.manualInvoice = this.createManualInvoiceModel(manualInvoiceId, invoiceNumber);
    manualPrelimInvoice.manualInvoiceCustomer = this.createManualInvoiceCustomerModel(manualInvoiceId);
    manualPrelimInvoice.billingLines = this.createManualInvoiceBillingLines(manualInvoiceId);

    return manualPrelimInvoice;
  }

  createManualInvoiceModel(manualInvoiceId: string, invoiceNumber: string): IManualInvoice_Request {
    let manualInvoice: IManualInvoice_Request = {} as IManualInvoice_Request;

    manualInvoice.manualInvoiceId = manualInvoiceId;
    manualInvoice.lotNumber = this._appGeneralState.currentLotNumber;
    manualInvoice.recipient = this.getRecipientCustomerName();
    manualInvoice.invoiceDate = (this.preLimMiForm.get('invoiceDate')?.value ?? new Date()) as Date;
    manualInvoice.dueDate = (this.preLimMiForm.get('dueDate')?.value ?? new Date()) as Date;
    manualInvoice.invoiceDescription = this.invoiceDescription;
    manualInvoice.created = new Date();
    manualInvoice.createdBy = this._appGeneralState.userName;
    manualInvoice.invoiceNumber = invoiceNumber;

    return manualInvoice;
  }

  createManualInvoiceCustomerModel(manualInvoiceId: string): IManualInvoiceCustomer_Request {
    let manualInvoiceCustomer: IManualInvoiceCustomer_Request = {} as IManualInvoiceCustomer_Request;
    const selectedParker = this.selectedParker ?? ({} as IParkerActiveShortListResponse);
    const selectedCompany = this.selectedCompany ?? ({} as ICompanyActiveShortListResponse);
    var billingAddress = '';
    var city = '';
    var state = '';
    var zipCode = '';
    var phone = '';
    var emailAddress = '';

    if (this.currentSearchType == 'parker') {
      billingAddress = selectedParker.address;
      city = selectedParker.city;
      state = selectedParker.state;
      zipCode = selectedParker.zipCode;
      phone = selectedParker.phone;
      emailAddress = selectedParker.emailAddress;
    } else {
      billingAddress = selectedCompany.address;
      city = selectedCompany.city;
      state = selectedCompany.state;
      zipCode = selectedCompany.zipCode;
      phone = selectedCompany.phone;
      emailAddress = selectedCompany.emailAddress;
    }

    manualInvoiceCustomer.manualInvoiceId = manualInvoiceId;
    manualInvoiceCustomer.customerType = this.currentSearchType == 'parker' ? 'Parker' : 'Company';
    manualInvoiceCustomer.entityId =
      this.currentSearchType == 'parker' ? this.selectedParkerId : this.selectedCompanyId;
    manualInvoiceCustomer.customerName = this.getRecipientCustomerName();
    manualInvoiceCustomer.billingAddress = billingAddress;
    manualInvoiceCustomer.city = city;
    manualInvoiceCustomer.state = state;
    manualInvoiceCustomer.zipCode = zipCode;
    manualInvoiceCustomer.phone = phone;
    manualInvoiceCustomer.emailAddress = emailAddress;

    return manualInvoiceCustomer;
  }

  createManualInvoiceBillingLines(manualInvoiceId: string): IBillingDetailWithTax_Request[] {
    let manualInvoiceBillingLines: IBillingDetailWithTax_Request[] = [];

    let ii: number = 0;
    for (ii = 0; ii < this.miFormData.length; ii++) {
      let newBilliingLine: IBillingDetailWithTax_Request = {} as IBillingDetailWithTax_Request;
      const item: IMiNewInvoiceLine = this.miFormData[ii];

      newBilliingLine.ManualInvoiceId = manualInvoiceId;
      newBilliingLine.paymentType = item.revenueCode;
      newBilliingLine.billingRate = item.rate;
      newBilliingLine.quantity = item.quantity;
      newBilliingLine.flatRate = item.rate;
      newBilliingLine.sequenceStart = item.sequenceStart;
      newBilliingLine.sequenceEnd = item.sequenceEnd;
      newBilliingLine.tax = item.taxAmount;
      newBilliingLine.description = item.description;
      newBilliingLine.revCode = item.revenueType;
      newBilliingLine.taxType = 'Total Tax'; //todo
      newBilliingLine.taxAmount = item.taxAmount;
      newBilliingLine.taxAmountMoney = item.taxAmount;

      manualInvoiceBillingLines.push(newBilliingLine);
    }

    return manualInvoiceBillingLines;
  }

  showInvoicePdf(invoiceNumber: string, entityId: string): void {
    const safeEntityId = entityId && entityId.trim().length > 0 ? entityId : 'na';

    console.log('safe entity id', safeEntityId);

    const urlTree = this._router.createUrlTree([
      '/finalizeinvoice',
      this.currentLotNumber,
      safeEntityId,
      invoiceNumber,
    ]);
    const url = this._router.serializeUrl(urlTree);
    window.open(url, '_blank');
  }

  onDisplayPrelimInvoice(invoiceNumber: string, entityId: string) {
    const lotNumber = this._appGeneralState.currentLotNumber;
    this._dataService.GetMiInvoicePdf(invoiceNumber, lotNumber, entityId).subscribe((response) => {
      const blob = new Blob([response.body!], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url); // or use an <a> to trigger download
    });
  }

  async setInvoiceDefaultValues(lotNumber: string): Promise<void> {
    this.invoiceSystemDefaults = await firstValueFrom(this._dataService.invoiceCreation_GetSystemDefaults(lotNumber));

    console.log('invoiceSystemDefaults', this.invoiceSystemDefaults);
  }

  // Instead of previewing the .pdf invoice, this will generat a prelim and then finalize.
  // After a successful finalization, if the parker/customer has an email address, it will display a dialog so the user can email the invoice to the user.
  // If no email address, display a message stating finalization complete but no invoice available to email.
  async onGenerateAndFinalizeInvoice(): Promise<void> {
    await this.createPrelimAndFinalizeInvoice();
  }

  async createPrelimAndFinalizeInvoice(): Promise<boolean> {
    var isFinalized: boolean = false;

    try {
      // Validation: Ensure a parker/company is selected
      if (!this.targetForInvoiceSelected) {
        this._matSnackBar.open('Please select a Parker or Company before generating an invoice.', 'Close', {
          horizontalPosition: 'center',
          verticalPosition: 'top',
          duration: 5000,
          panelClass: 'error-snackbar',
        });
        return false;
      }

      // Validation: Ensure the selected entity has required data
      const selectedEntity = this.currentSearchType === 'parker' ? this.selectedParker : this.selectedCompany;
      if (!selectedEntity) {
        this._matSnackBar.open('Invalid selection. Please select a Parker or Company again.', 'Close', {
          horizontalPosition: 'center',
          verticalPosition: 'top',
          duration: 5000,
          panelClass: 'error-snackbar',
        });
        return false;
      }

      // Step 1: Create the prelim invoice. The server generates the invoice number, so everything
      // downstream has to use the one it returns rather than a locally generated value.
      const newInvoiceNumber: string = await this.createPrelimInvoice();
      if (!newInvoiceNumber) {
        throw new Error('Invoice finalization failed at prelim creation step.');
      }

      // Step 2: Finalize the prelim invoice
      const isPrelimFinalized: boolean = await this.finalizeInvoiceHttp(
        newInvoiceNumber,
        this._appGeneralState.currentLotNumber,
      );
      if (!isPrelimFinalized) {
        throw new Error('Invoice finalization failed at invoice finalization step.');
      }

      // Step 3: If finalized successfully, check for customer email and display appropriate message/dialog
      this.deliverySettings = await this.getRecipientNotificationDeliverySettings(newInvoiceNumber);

      if (this.isCapableOfSendingEmail) {
        const recipientEmailAddress = this.deliverySettings?.recipientEmailAddress ?? '';
        const dialogRef = this.#_matDialog.open(DialogMiSendInvoiceComponent, {
          data: { emailAddress: recipientEmailAddress },
          width: '500px',
        });
        dialogRef.afterClosed().subscribe((result: boolean) => {
          if (result) {
            this.sendInvoiceNotification(newInvoiceNumber);
          }
        });
      } else {
        const snackBarRef = this._matSnackBar.open(
          'This invoice has been finalized, but it cannot be emailed because no email address is available, or the delivery method is set to print.',
          '',
          {
            horizontalPosition: 'center',
            verticalPosition: 'top',
            duration: 5000,
            panelClass: 'centered-snackbar',
          },
        );
      }
      this.resetFormAfterFinalization(); // Reset component to initial state with same lot number to allow user to create another MI if needed.
      isFinalized = true;
    } catch (err) {
      console.error('Error during invoice finalization', err);
    }

    return isFinalized;
  }

  private onUpdateInvoiceSendDate(invoiceNumber: string): void {
    const dateSent = new Date();
    this._dataService
      .updateInvoiceNotificationEmailDateSent(invoiceNumber, dateSent.toISOString().split('T')[0])
      .pipe(takeUntilDestroyed(this._destroyRefService))
      .subscribe();
  }

  /// Resolves to the invoice number the server assigned, or an empty string on failure.
  createPrelimInvoice(): Promise<string> {
    return new Promise<string>((resolve) => {
      const newMiPrelimData: IMiInvoiceSubmitRequest = this.createNewMiPrelimModel();
      let invoiceDataResponse: Mi_PrelimInvoiceCreationReturn;

      // Set entityID for this flow
      this.currentEntityId = newMiPrelimData.invoiceCustomer.entityId;

      const snackBarRef = this._matSnackBar.open('Generating and Finalizng a new Manual Invoice', '', {
        horizontalPosition: 'center',
        verticalPosition: 'top',
        duration: 3000,
        panelClass: 'centered-snackbar',
      });

      this._dataService
        .mi_GeneratePrelimInvoices(newMiPrelimData)
        .pipe(takeUntilDestroyed(this._destroyRefService))
        .subscribe({
          next: (res) => {
            invoiceDataResponse = res;
          },
          error: (_err: any) => {
            snackBarRef.dismiss();
            this._matSnackBar.open('An error occurred while creating this Manual Invoice. Please try again.', 'Close', {
              horizontalPosition: 'center',
              verticalPosition: 'top',
              duration: 5000,
              panelClass: 'error-snackbar',
            });
            resolve('');
          },
          complete: () => {
            snackBarRef.dismiss();

            if (!invoiceDataResponse?.status || !invoiceDataResponse.invoiceNumber) {
              this._matSnackBar.open(
                invoiceDataResponse?.errorMessage ||
                  'An error occurred while creating this Manual Invoice. Please try again.',
                'Close',
                {
                  horizontalPosition: 'center',
                  verticalPosition: 'top',
                  duration: 5000,
                  panelClass: 'error-snackbar',
                },
              );
              resolve('');
              return;
            }

            resolve(invoiceDataResponse.invoiceNumber);
          },
        });
    });
  }

  // Finalize a prelim invoice
  finalizeInvoiceHttp(invoiceNumber: string, lotNumber: string): Promise<boolean> {
    let isFinalized: boolean = false;
    return new Promise<boolean>((resolve) => {
      this._dataService
        .mi_FinalizePrelimInvoice(invoiceNumber, lotNumber)
        .pipe(takeUntilDestroyed(this._destroyRefService))
        .subscribe({
          next: (res) => {
            isFinalized = res;
          },
          error: (err: any) => {
            isFinalized = false;
            this._matSnackBar.open(
              'An error occurred while finalizing this Manual Invoice. Please try again.',
              'Close',
              {
                horizontalPosition: 'center',
                verticalPosition: 'top',
                duration: 5000,
                panelClass: 'error-snackbar',
              },
            );
            resolve(false);
          },
          complete: () => {
            resolve(isFinalized);
          },
        });
    });
  }

  async getRecipientNotificationDeliverySettings(
    invoiceNumber: string,
  ): Promise<IInvoicePrelimRecipientNotificationDetailsResponse | null> {
    try {
      this.deliverySettings = await firstValueFrom(
        this._dataService.getPrelimInvoiceDetailsForRecipientNotificationEmail(invoiceNumber),
      );

      this.isCapableOfSendingEmail =
        this.deliverySettings != null &&
        this.deliverySettings.recipientEmailAddress !== '' &&
        this.deliverySettings.deliveryMethod.toLowerCase() === 'email';
    } catch (_err: any) {
      this.deliverySettings = null;
      this.isCapableOfSendingEmail = false;
    }

    return this.deliverySettings;
  }

  sendInvoiceNotification(invoiceNumber: string): void {
    const processingSnackRef = this._matSnackBar.open('Stand by....Sending Invoice.', '', {
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: 'centered-snackbar',
    });

    let emailSentSuccessfully = false;

    this._dataService
      .sendInvoiceNotificationEmail(invoiceNumber)
      .pipe(takeUntilDestroyed(this._destroyRefService))
      .subscribe({
        next: (res) => {
          emailSentSuccessfully = res;
        },
        error: (_err: any) => {
          processingSnackRef.dismiss();
          this._matSnackBar.open(
            'An error occured while emailing the invoice but it has been finalized successfully.',
            'Close',
            {
              horizontalPosition: 'center',
              verticalPosition: 'top',
              duration: 5000,
              panelClass: 'error-snackbar',
            },
          );
          this.resetFormAfterFinalization();
        },
        complete: () => {
          processingSnackRef.dismiss();
          this.onUpdateInvoiceSendDate(invoiceNumber); // Update the send date for the invoice notification email to today since the email was sent successfully.

          if (emailSentSuccessfully) {
            this._matSnackBar.open(
              'The Invoice has been successfully emailed to the customer.  You may generate another Manual Invoice now.',
              '',
              {
                horizontalPosition: 'center',
                verticalPosition: 'top',
                duration: 5000,
                panelClass: 'centered-snackbar',
              },
            );
            this.resetFormAfterFinalization();
            return;
          }

          this._matSnackBar.open(
            'An error occured while emailing the invoice but it has been finalized successfully.',
            'Close',
            {
              horizontalPosition: 'center',
              verticalPosition: 'top',
              duration: 5000,
              panelClass: 'error-snackbar',
            },
          );

          this.resetFormAfterFinalization();
        },
      });
  }

  // After finalization, reset the form to allow for a new invoice to be created under the same lot without having to change the lot number.
  // Created this method in the future case that the reset was different than the lot change change.
  resetFormAfterFinalization(): void {
    this.onLotNumberChange(this.currentLotNumber);
  }
}
